import { useState } from 'react';

import styled from 'styled-components';

import { EmptyState } from '@components/empty-state';
import { IconEmailAddress } from '@components/icons/email-address';
import { RelativeSpinner } from '@components/loading-spinner';
import { PageContainers } from '@components/page-containers';
import { PageContent } from '@components/page-containers/page-container';
import { SearchBox } from '@components/search-box';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { useBlockedAddresses, useEmailDomains } from 'api/company/hooks';
import { EmailDomain } from 'api/company/requests';

import { BlockedAddressSection } from './blocked-address-section';
import { LoadFailedPanel } from './load-failed-panel';

export const BlockedAddressesTab = () => {
  const { t } = useScopedTranslate(locale);
  const domainsQuery = useEmailDomains();
  const managedDomains = (domainsQuery.data ?? []).filter((domain) => domain.isManaged);
  const [selectedShortId, setSelectedShortId] = useState('');
  const [search, setSearch] = useState('');

  const isSelectionValid = managedDomains.some((domain) => domain.shortId === selectedShortId);
  if (!isSelectionValid && managedDomains.length > 0) {
    setSelectedShortId(managedDomains[0].shortId);
  }
  const selectedDomain =
    managedDomains.find((domain) => domain.shortId === selectedShortId) ?? managedDomains[0];

  if (domainsQuery.isLoading) {
    return <RelativeSpinner />;
  }

  if (domainsQuery.isError && managedDomains.length === 0) {
    return (
      <LoadFailedPanel
        title={t('domainsLoadFailedTitle')}
        description={t('domainsLoadFailedDescription')}
        onRetry={() => domainsQuery.refetch()}
        isRetrying={domainsQuery.isFetching}
      />
    );
  }

  if (managedDomains.length === 0) {
    return (
      <EmptyState
        icon={<IconEmailAddress />}
        header={t('noManagedDomainsHeader')}
        content={t('noManagedDomainsContent')}
      />
    );
  }

  return (
    <PageContent>
      <PageContainers.MiddleColumnPage>
        <Stack space={6}>
          <Text text={t('description')} font="regular14" />

          <Stack orientation="horizontal" space={6} align="center" wrap>
            {managedDomains.length === 1 ? (
              <Text text={selectedDomain.domain} font="semibold12" />
            ) : (
              <DomainPicker>
                <Select
                  label={t('domainLabel')}
                  hideLabel
                  isClearable={false}
                  isSearchable={false}
                  value={selectedDomain.shortId}
                  options={managedDomains.map((domain) => ({
                    label: domain.domain,
                    value: domain.shortId,
                    tooltip: domain.domain,
                  }))}
                  onChange={setSelectedShortId}
                />
              </DomainPicker>
            )}
            <SearchSlot>
              <SearchBox isFullWidth onChange={setSearch} placeholder={t('searchPlaceholder')} />
            </SearchSlot>
          </Stack>

          <Separator orientation="vertical" />

          <BlockedAddressesContent
            key={selectedDomain.shortId}
            domain={selectedDomain}
            search={search}
          />
        </Stack>
      </PageContainers.MiddleColumnPage>
    </PageContent>
  );
};

type BlockedAddressesContentProps = {
  domain: EmailDomain;
  search: string;
};

const BlockedAddressesContent = ({ domain, search }: BlockedAddressesContentProps) => {
  const { t } = useScopedTranslate(locale);

  const bouncesQuery = useBlockedAddresses({ shortId: domain.shortId, category: 'bounces' });
  const unsubscribesQuery = useBlockedAddresses({
    shortId: domain.shortId,
    category: 'unsubscribes',
  });
  const complaintsQuery = useBlockedAddresses({ shortId: domain.shortId, category: 'complaints' });
  const aggregateQueries = [bouncesQuery, unsubscribesQuery, complaintsQuery];
  const isAggregateFetching = aggregateQueries.some((query) => query.isFetching);
  const aggregateCount = aggregateQueries.reduce(
    (sum, query) => sum + (query.data?.items.length ?? 0),
    0,
  );
  const allAggregateErrored =
    aggregateQueries.every((query) => query.isError) && aggregateCount === 0;

  const handleRetryAll = () => {
    bouncesQuery.refetch();
    unsubscribesQuery.refetch();
    complaintsQuery.refetch();
  };

  if (allAggregateErrored) {
    return (
      <LoadFailedPanel
        title={t('sectionsLoadFailedTitle')}
        description={t('sectionsLoadFailedDescription')}
        onRetry={handleRetryAll}
        isRetrying={isAggregateFetching}
      />
    );
  }

  return (
    <>
      <BlockedAddressSection
        category="bounces"
        title={t('bounced')}
        helper={t('bouncedHelp')}
        emptyText={t('bouncedEmpty')}
        canUnblock
        shortId={domain.shortId}
        search={search}
      />
      <Separator orientation="vertical" />
      <BlockedAddressSection
        category="unsubscribes"
        title={t('unsubscribed')}
        helper={t('unsubscribedHelp')}
        emptyText={t('unsubscribedEmpty')}
        shortId={domain.shortId}
        search={search}
      />
      <Separator orientation="vertical" />
      <BlockedAddressSection
        category="complaints"
        title={t('complained')}
        helper={t('complainedHelp')}
        emptyText={t('complainedEmpty')}
        shortId={domain.shortId}
        search={search}
      />
    </>
  );
};

const SearchSlot = styled.div({
  flex: '1 1 0',
  minWidth: `min(100%, ${pxToRem(160)})`,
});

const DomainPicker = styled.div({
  flex: '1 1 0',
  minWidth: `min(100%, ${pxToRem(200)})`,
});

const locale = {
  en: {
    description:
      'Addresses we have stopped sending to on this domain, read live from our email provider.',
    domainLabel: 'Domain',
    searchPlaceholder: 'Search…',
    bounced: 'Bounced',
    bouncedHelp:
      'Delivery failed. Unblocking lets us try again — if it bounces again it is blocked again automatically',
    bouncedEmpty: 'No addresses bounced',
    unsubscribed: 'Unsubscribed',
    unsubscribedHelp:
      'People who have opted out. We keep them blocked so their choice is respected',
    unsubscribedEmpty: 'No addresses unsubscribed',
    complained: 'Marked as spam',
    complainedHelp:
      'People who report a message as spam. We keep them blocked so their choice is respected',
    complainedEmpty: 'No addresses marked as spam',
    noManagedDomainsHeader: 'No domains hosted by us',
    noManagedDomainsContent:
      'Blocked addresses are only tracked for domains we host (DNS). Add one to see them here.',
    domainsLoadFailedTitle: 'Could not load domains',
    domainsLoadFailedDescription: "We couldn't load your domains. Refresh the page or try again.",
    sectionsLoadFailedTitle: 'Could not load blocked addresses',
    sectionsLoadFailedDescription: 'Our email provider did not respond. Try again in a moment.',
  },
};
